import { Config } from "services/configuration/eatSmartConfig";
import type { Maybe } from "types";
import { validate as uuidValidate } from "uuid";

/**
 * `localStorage` access can throw (e.g. `SecurityError` when storage is blocked, or
 * `QuotaExceededError` on write). These wrappers keep the failure local so it can never
 * bubble into a React render or a module's top-level evaluation and crash the app — a read
 * failure degrades to "no stored value" and a write failure becomes a no-op.
 *
 * They intentionally do not log: importing the logger here would create an import cycle
 * (logger → shouldEnableFeature → localStorage), and the missing value is already handled by
 * every caller.
 */
const safeGetItem = (key: string): string | null => {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
};

const safeSetItem = (key: string, value: string): void => {
	try {
		localStorage.setItem(key, value);
	} catch {
		// ignore: persisting is best-effort
	}
};

const safeRemoveItem = (key: string): void => {
	try {
		localStorage.removeItem(key);
	} catch {
		// ignore: removal is best-effort
	}
};

/**
 * Sets new fridgeId in localStorage, updates URL search parameter, and reloads the page
 * @param currentFridgeId Optional current fridgeId to show in prompt
 * @param errorMessage Optional error message to show in prompt
 */
export const setupNewFridgeId = (currentFridgeId?: string, errorMessage?: string) => {
	const message = errorMessage
		? errorMessage
		: currentFridgeId
			? `Current fridgeId "${currentFridgeId}" is invalid. Please provide a valid UUID`
			: "Please provide a valid fridgeId UUID";

	// biome-ignore lint/suspicious/noAlert: intentional use of prompt for fridge ID setup
	const newFridgeId = prompt(message);

	if (newFridgeId === null) {
		// If user cancels the prompt, remove the fridgeId from localStorage
		safeRemoveItem("fridgeId");
	} else if (validateFridgeId(newFridgeId)) {
		safeSetItem("fridgeId", newFridgeId);

		// Update URL search parameter
		const searchParams = new URLSearchParams(window.location.search);
		searchParams.set("fridgeId", newFridgeId);

		// Update the URL and reload the page
		const newUrl = new URL(window.location.href);
		newUrl.search = searchParams.toString();
		window.location.href = newUrl.toString();
	} else {
		// Recursively ask for valid UUID if input is invalid
		setupNewFridgeId(newFridgeId);
	}
};

/**
 * Gets query parameter from URL or localStorage.
 * @param paramName Query parameter name.
 * @param paramDefaultValue Parameter default value.
 * @returns Current parameter value.
 */
const getQueryParamByName = (paramName: string, paramDefaultValue: string) => {
	const paramFromUrl = new URLSearchParams(window.location.search).get(paramName);
	const paraFromLocalStorage = safeGetItem(paramName);

	// Return the param from local storage if available
	if (paraFromLocalStorage !== null && paramFromUrl === null) {
		return paraFromLocalStorage;
	}

	// Update local storage with the query param and return it
	if (paramFromUrl !== null) {
		safeSetItem(paramName, paramFromUrl);
		return paramFromUrl;
	}

	safeSetItem(paramName, paramDefaultValue);
	return paramDefaultValue;
};

/**
 * Returns fridgeId from query params or localStorage
 */
export const getFridgeId = () => {
	const fridgeId = getQueryParamByName("fridgeId", "");
	const isUuidValid = validateFridgeId(fridgeId);

	if (isUuidValid) {
		return fridgeId;
	}

	// Set up a new fridge ID if none is found or if it's invalid
	setupNewFridgeId(fridgeId);
	return undefined;
};

export enum TabletTypeEnum {
	Master = "master",
	Standard = "standard",
}

export const getTabletTypeFromUrl = (): TabletTypeEnum => {
	const tabletTypeFromQueryParam = new URLSearchParams(window.location.search).get("tabletType");
	const tabletType =
		tabletTypeFromQueryParam === TabletTypeEnum.Master ? TabletTypeEnum.Master : TabletTypeEnum.Standard;
	safeSetItem("tabletType", tabletType);
	return tabletType;
};

export const getTabletType = () => {
	const tabletTypeFromLocalStorage = safeGetItem("tabletType");

	if (tabletTypeFromLocalStorage !== null) {
		return tabletTypeFromLocalStorage;
	}

	// Resolve from the URL AND return it. Without this return the first call (empty cache) yielded
	// undefined, so the lighting converger's master guard bailed on its first run until it self-healed.
	return getTabletTypeFromUrl();
};

/**
 * Gets current instance locale. Primary source is URL.
 * @returns Current instance default locale.
 */
export const getLocale = () => getQueryParamByName(Config.common.localeLocalStorageKey, Config.common.defaultLocale);
export const getIsMasterTablet = () => {
	const tabletType = getTabletType();
	return tabletType === TabletTypeEnum.Master;
};

export const setFridgeNameToLocalStorage = (fridgeName: Maybe<string>) => {
	const currentFridgeName = safeGetItem("fridgeName");
	if (fridgeName && currentFridgeName !== fridgeName) {
		safeSetItem("fridgeName", fridgeName);
	}
};

export const getFridgeName = () => safeGetItem("fridgeName");

/**
 * Validates fridgeId and provides appropriate feedback
 * @param fridgeId The fridgeId to validate
 * @returns True if valid UUID, false otherwise
 */
export const validateFridgeId = (fridgeId: string): boolean => {
	if (!fridgeId || fridgeId.length === 0) {
		return false;
	}
	return uuidValidate(fridgeId);
};
